import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Form, Input, Typography, Upload } from 'antd';
import { InboxOutlined, PlusOutlined } from '@ant-design/icons';
import { createProject } from '../illustration/api';

const { Title, Paragraph, Text } = Typography;
const { Dragger } = Upload;

function parseBibleFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      const name = (file.name || '').toLowerCase();
      if (name.endsWith('.json')) {
        try {
          resolve({ kind: 'json', bible: JSON.parse(text) });
        } catch (e) {
          reject(new Error(`Invalid JSON bible: ${e.message}`));
        }
        return;
      }
      resolve({ kind: 'markdown', markdown: text });
    };
    reader.onerror = () => reject(new Error('Could not read bible file'));
    reader.readAsText(file);
  });
}

export default function CreateProjectPage() {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [bibleFile, setBibleFile] = useState(null);
  const [biblePreview, setBiblePreview] = useState(null);

  const onFile = async (file) => {
    setError(null);
    const name = (file?.name || '').toLowerCase();
    if (!/\.(json|md|markdown|txt)$/.test(name)) {
      setBibleFile(null);
      setBiblePreview(null);
      setError('Choose a .json, .md, .markdown, or .txt Illustration Bible.');
      return false;
    }
    try {
      const parsed = await parseBibleFile(file);
      setBibleFile(file);
      setBiblePreview(parsed);
    } catch (e) {
      setBibleFile(null);
      setBiblePreview(null);
      setError(e.message);
    }
    return false;
  };

  const onCreate = async (values) => {
    if (!biblePreview) {
      setError('Upload an Illustration Bible (.json or .md) to seed the project.');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const biblePayload =
        biblePreview.kind === 'json'
          ? { ...biblePreview.bible, version: values.bibleVersion || biblePreview.bible.version || '1.0' }
          : {
              markdown: biblePreview.markdown,
              version: values.bibleVersion || '1.0',
              title: `${values.title} — Illustration Bible`,
            };

      const { project } = await createProject({
        title: values.title,
        notes: values.notes || '',
        bible: biblePayload,
      });

      window.dispatchEvent(new Event('artificiallly:projects-changed'));
      navigate(`/projects/${project.id}/chapters`);
    } catch (e) {
      setError(e.message || 'Could not create project');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="workflow-shell__content">
      <Title
        level={1}
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-display)',
          letterSpacing: 'var(--tracking-display)',
          marginBottom: 'var(--space-2)',
        }}
      >
        Create New
      </Title>
      <Paragraph style={{ color: 'var(--color-text-secondary)', maxWidth: 'var(--measure-prose)' }}>
        Start a book project by importing an Illustration Bible. The bible seeds style,
        constraints, characters, and references for generation.
      </Paragraph>

      {error && (
        <Alert type="error" showIcon title={error} style={{ marginBottom: 16 }} />
      )}

      <Form
        layout="vertical"
        onFinish={onCreate}
        style={{ maxWidth: 520 }}
        requiredMark="optional"
      >
        <Form.Item
          name="title"
          label="Project title"
          rules={[{ required: true, message: 'Title is required' }]}
        >
          <Input placeholder="e.g. Adventure Awaits" autoFocus />
        </Form.Item>

        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} placeholder="Optional production notes" />
        </Form.Item>

        <Form.Item name="bibleVersion" label="Bible version" initialValue="1.0">
          <Input placeholder="1.0" />
        </Form.Item>

        <Form.Item
          label="Illustration Bible"
          extra="Upload a structured .json bible, or .md / .txt source to ingest and seed the project."
        >
          <Dragger
            multiple={false}
            maxCount={1}
            beforeUpload={onFile}
            onRemove={() => {
              setBibleFile(null);
              setBiblePreview(null);
            }}
            fileList={
              bibleFile
                ? [
                    {
                      uid: 'bible',
                      name: bibleFile.name,
                      status: 'done',
                    },
                  ]
                : []
            }
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Drop Illustration Bible here</p>
            <p className="ant-upload-hint">JSON, Markdown, or text</p>
          </Dragger>
        </Form.Item>

        {biblePreview && (
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Ready to seed:{' '}
            {biblePreview.kind === 'json'
              ? `JSON bible${biblePreview.bible?.title ? ` — ${biblePreview.bible.title}` : ''}`
              : `Markdown source (${biblePreview.markdown.length} chars)`}
          </Text>
        )}

        <Button
          type="primary"
          htmlType="submit"
          icon={<PlusOutlined />}
          loading={creating}
          size="large"
        >
          Create project
        </Button>
      </Form>
    </div>
  );
}
